import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sandtech-hub-monorepo/ui-kit';

import { PageHero } from '../../components/PageHero';
import { EnrolledProgramList } from '../../components/program/EnrolledProgramList';
import { ProgramsCatalogueBody } from '../ProgramsCatalogue/components/ProgramsCatalogueBody';
import learningHero from '../../assets/gsa/learning-hero.webp';
import { EmptyTab } from './components/EmptyTab';
import { useGsaEnrollments } from '../../data/gsaEnrollments';
import { useAchievements } from '../../data/useAchievements';
import { AppRoutesPaths, LearningTabSections } from '../../routes/types';
import { PastProgramRow } from './components/PastProgramRow';
import { AchievementCard } from '../../components/achievements/AchievementCard';
import { AchievementsEmptyState } from '../../components/achievements/AchievementsEmptyState';

const TABS: { value: LearningTabSections; label: string }[] = [
  { value: LearningTabSections.Current, label: 'Current Programs' },
  { value: LearningTabSections.Past, label: 'Past Programs' },
  { value: LearningTabSections.All, label: 'All Programs' },
  { value: LearningTabSections.Achievements, label: 'My Achievements' },
];

const tabItemClassName =
  'shrink-0 whitespace-nowrap rounded-none relative border-b-2 border-b-transparent bg-transparent px-4 py-3 text-base leading-5 font-medium text-heading shadow-none transition-none data-[state=active]:text-primary data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none';

export const GsaLearning = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { current: enrolled, past: completedPrograms } = useGsaEnrollments();
  const { achievements, loading: achievementsLoading } = useAchievements();

  const validTabs = TABS.map((t) => t.value);
  const current = searchParams.get('tab') as LearningTabSections;
  const tab = validTabs.includes(current)
    ? current
    : LearningTabSections.Current;

  return (
    <div className="bg-background pb-16">
      <PageHero
        backgroundImage={learningHero}
        showDecorations
      >
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-yellow">
          Welcome to Global Skills Academy
        </p>
        <h1 className="mt-2 max-w-3xl text-3xl font-semibold md:text-4xl">
          This is your learning space.
        </h1>
        <p className="mt-3 text-white/90">
          Come back anytime to pick up where you left off.
        </p>
      </PageHero>

      <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-8 md:px-12 py-8">
        <Tabs
          value={tab}
          onValueChange={(value) =>
            setSearchParams({ tab: value }, { replace: true })
          }
          className="w-full"
        >
          <TabsList className="flex w-full justify-start overflow-x-auto rounded-none border-b border-border bg-transparent p-0 h-auto">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className={tabItemClassName}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={LearningTabSections.Current} className="mt-6">
            {enrolled.length ? (
              <div className="flex flex-col gap-4">
                <EnrolledProgramList enrollments={enrolled} />
              </div>
            ) : (
              <EmptyTab message="You're not enrolled in any programs yet." />
            )}
          </TabsContent>

          <TabsContent value={LearningTabSections.Past} className="mt-6">
            {completedPrograms.length ? (
              <div className="flex flex-col gap-4">
                {completedPrograms.map((p) => (
                  <PastProgramRow
                    key={p.id}
                    program={p}
                    onViewDetails={() =>
                      navigate(
                        AppRoutesPaths.Program.replace(
                          ':programId',
                          p.abbreviation
                        )
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyTab message="You haven't completed any programs yet." />
            )}
          </TabsContent>

          <TabsContent value={LearningTabSections.All} className="mt-6">
            <ProgramsCatalogueBody type="gsa" />
          </TabsContent>

          <TabsContent
            value={LearningTabSections.Achievements}
            className="mt-6"
          >
            {achievements.length ? (
              <div className="flex flex-col gap-4">
                {achievements.map((enrollment) => (
                  <AchievementCard
                    key={enrollment.id}
                    enrollment={enrollment}
                  />
                ))}
              </div>
            ) : (
              !achievementsLoading && <AchievementsEmptyState />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
